import { useEffect, useState } from 'react'

import * as XLSX from 'xlsx'

import { MESSAGE_TYPE, useFloatMessage } from './../../../Providers/FloatMessage'
import { SecondaryBase } from './../../Commons/SecondaryBase'
import { GetMessagesPage as BLGetMessagesPage } from './../../../../utilities/BL/Admin/Overlays/Messages'


// ==============================================================================
// CONFIGURACIÓN Y UTILIDADES
// ==============================================================================

const PAGE_SIZE = 30;

const formatFullDate = (dateString) => {
    if (!dateString) return "Fecha no disponible";
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }) + " hrs";
};

/**
 * COMPONENTE: MessagesBoard
 * Descripción: Tablero exclusivo para consultar CONTACTOS (Mensajes con teléfono).
 */
const MessagesBoard = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // ----------------------------------------------------------------------------
    // 1. ESTADOS
    // ----------------------------------------------------------------------------
    const [messagesList, setMessagesList] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState('DESC');
    
    // YA NO NECESITAMOS EL ESTADO DEL SWITCH, PORQUE SIEMPRE SERÁ TRUE

    // ----------------------------------------------------------------------------
    // 2. LÓGICA DE CARGA DE DATOS
    // ----------------------------------------------------------------------------

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            // SOLICITUD A LA API: onlyWithPhone: true
            const response = await BLGetMessagesPage({ 
                limit: PAGE_SIZE, 
                cursor: null, 
                sort: sortOrder, 
                onlyWithPhone: true
            });
            
            if (!response.success) throw new Error(response.message);

            setMessagesList(response.data.items || []);
            setNextCursor(response.data.nextCursor); // Si es null, significa que no hay más
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Lista de contactos cargada con éxito' });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Ha habido un error al obtener la información de los contactos. Actualiza la página o inténtalo más tarde' });
        } finally {
            setIsLoading(false);
        }
    };

    // const loadMoreMessages = async () => {
    //     if (!nextCursor) return; // Seguridad extra
    //     if (isLoading) return;

    //     setIsLoading(true);
    //     try {
    //         const response = await BLGetMessagesPage({ 
    //             limit: PAGE_SIZE, 
    //             cursor: nextCursor, 
    //             sort: sortOrder,
    //             onlyWithPhone: true 
    //         });

    //         if (!response.success) throw new Error(response.message);

    //         setMessagesList(prev => [...prev, ...response.data.items]);
    //         setNextCursor(response.data.nextCursor);
    //     } catch (error) {
    //         showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar más historial.' });
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const loadMoreMessages = async () => {
        // 1. Si ya no hay cursor (la API nos dijo antes que era el final), mostramos el mensaje y paramos.
        if (!nextCursor) {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay más contactos nuevos' });
            return;
        }
        
        if (isLoading) return;

        setIsLoading(true);
        try {
            // Solicitud a la API
            const response = await BLGetMessagesPage({ 
                limit: PAGE_SIZE, 
                cursor: nextCursor, 
                sort: sortOrder,
                onlyWithPhone: true 
            });

            if (!response.success) throw new Error(response.message);

            const newItems = response.data.items || [];

            // 2. Agregamos los nuevos a la lista
            if (newItems.length > 0) {
                setMessagesList(prev => [...prev, ...newItems]);

                if (newItems.length === 1) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Se han cargado 1 nuevo contacto` });
                } else {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Se han cargado ${newItems.length} contacto(s) nuevos` });
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'No se encontraron contactos nuevos' });
            }

            // 3. Actualizamos el cursor para la próxima vez
            setNextCursor(response.data.nextCursor);

        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al cargar más contactos. Inténtalo nuevamente o inténtalo más tarde' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { loadInitialData(); }, [sortOrder]);

    // ----------------------------------------------------------------------------
    // 3. EXPORTAR Y COPIAR
    // ----------------------------------------------------------------------------

    const copyPhoneNumberToClipboard = (phoneNumber) => {
        if (!phoneNumber) return;
        navigator.clipboard.writeText(phoneNumber)
            .then(() => showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Copiado al portapapeles.' }))
            .catch(() => showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al copiar. Inténtalo nuevamente o inténtalo más tarde' }));
    };

    const downloadExcelReport = async () => {
        if (isLoading) return;
        setIsLoading(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Exportando listado contactos...' });

        try {
            // Pedimos TODOS los contactos (Límite alto)
            const response = await BLGetMessagesPage({ 
                limit: 5000, 
                cursor: null, 
                sort: 'DESC', 
                onlyWithPhone: true 
            });
            
            if (!response.success || !response.data.items.length) {
                throw new Error("No hay contactos por exportar");
            }

            const dataToExport = response.data.items.map(item => ({
                ID: item.id,
                Fecha: formatFullDate(item.createdAtUtc || item.date),
                Telefono: item.phone, // Columna prioritaria
                Mensaje: item.message,
                Remitente: item.sender || "Anónimo"
            }));

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Contactos");

            const dateStr = new Date().toISOString().slice(0,10);
            XLSX.writeFile(workbook, `BD_Contactos_${dateStr}.xlsx`);

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Excel generado correctamente' });

        } catch (error) {
            console.error(error);
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReturn = () => { if (getOutOfTheDynamic) getOutOfTheDynamic(); };

    // ----------------------------------------------------------------------------
    // 4. RENDERIZADO
    // ----------------------------------------------------------------------------
    return (
        <SecondaryBase actionToExecute={handleReturn}>
            
            {/* --- ENCABEZADO --- */}
            <div className='d-flex flex-column px-4 mt-3'>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <p className='Admin__Messages__Text--Title'><i className='bi bi-person-lines-fill'></i> Contactos</p>
                        <p className='Admin__Messages__Text--Small'>Total cargados: {messagesList.length}</p>
                    </div>
                    
                    <button type="button" className='Btn__Adsl--RedOutlineTransparent' onClick={downloadExcelReport} disabled={isLoading}>
                        <i className='bi bi-file-earmark-excel-fill text-success'></i> Exportar contactos a Excel
                    </button>
                </div>

                {/* Filtros de Orden */}
                <div className='d-flex justify-content-end align-items-center border-bottom pb-3 border-secondary'>
                    <span className="me-2 small">Ordenar:</span>
                    <div className="btn-group btn-group-sm">
                        <button type="button" className={`btn ${sortOrder === 'ASC' ? 'btn-danger' : 'btn-outline-secondary'}`} onClick={() => setSortOrder('ASC')}>Antigüos</button>
                        <button type="button" className={`btn ${sortOrder === 'DESC' ? 'btn-danger' : 'btn-outline-secondary'}`} onClick={() => setSortOrder('DESC')}>Nuevos</button>
                    </div>
                </div>
            </div>

            {/* --- LISTADO --- */}
            <section className='Admin__Messages__Library Admin__Messages__SpaceUp'>
                {messagesList.length > 0 ? (
                    messagesList.map((msg) => (
                        <div key={msg.id} className="Admin__Messages__Library__Message" style={{ height: 'auto', minHeight: '220px' }}>
                            <div className="d-flex flex-column justify-content-between h-100">
                                
                                {/* Header Tarjeta */}
                                <div className="text-center border-bottom border-secondary pb-2 mb-2">
                                    <p className='Admin__Messages__Text--Small fw-bold mb-0'>#{msg.id}</p>
                                    <p className='small text-muted mb-0' style={{ fontSize: '0.7rem' }}>
                                        {formatFullDate(msg.date || msg.createdAtUtc)}
                                    </p>
                                </div>

                                {/* Teléfono (Destacado) */}
                                <div className="text-center py-2">
                                    <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                                        <span className="fs-6">
                                            <i className="bi bi-whatsapp"></i>/<i className="bi bi-phone me-1"></i>{msg.phone}
                                        </span>
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-outline-success" 
                                            onClick={() => copyPhoneNumberToClipboard(msg.phone)}
                                            title="Copiar"
                                        >
                                            <i className="bi bi-clipboard"></i>
                                        </button>
                                    </div>
                                </div>

                                {/* Contexto (Mensaje) */}
                                <div className="text-center flex-grow-1 d-flex flex-column justify-content-center px-2">
                                    <p className="small fst-italic text-muted mb-1">"{msg.message}"</p>
                                    <p className="text-info small mb-0">- {msg.sender || "Anónimo"}</p>
                                </div>

                            </div>
                        </div>
                    ))
                ) : (
                    <div className="w-100 text-center py-5">
                        <p className="text-muted fs-4">No hay contactos registrados.</p>
                        <button type="button" className="Btn__Adsl--RedOutlineTransparent" onClick={loadInitialData}>Recargar</button>
                    </div>
                )}
            </section>

            {/* --- BOTÓN CARGAR MÁS (SIEMPRE VISIBLE) --- */}
            <div className="d-flex justify-content-center my-5">
                <button 
                    type="button" 
                    className="Btn__Adsl--RedOutlineTransparent px-5" 
                    onClick={loadMoreMessages} 
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <> <span className="spinner-border spinner-border-sm me-2"></span> Cargando... </>
                    ) : (
                        <> <i className="bi bi-arrow-clockwise"></i> Cargar más mensajes </>
                    )}
                </button>
            </div>

        </SecondaryBase>
    );
};

export { MessagesBoard };






























// // ==============================================================================
// // CONFIGURACIÓN Y UTILIDADES
// // ==============================================================================

// const PAGE_SIZE = 30;

// const formatFullDate = (dateString) => {
//     if (!dateString) return "Fecha no disponible";
//     const date = new Date(dateString);
//     return date.toLocaleString('es-MX', {
//         day: '2-digit', month: '2-digit', year: 'numeric',
//         hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
//     }) + " hrs";
// };

// /**
//  * COMPONENTE: MessagesBoard
//  * Descripción: Tablero de consulta y reporte con filtrado por teléfono.
//  */
// const MessagesBoard = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage();

//     // ----------------------------------------------------------------------------
//     // 1. ESTADOS
//     // ----------------------------------------------------------------------------
//     const [messagesList, setMessagesList] = useState([]);
//     const [nextCursor, setNextCursor] = useState(null);
//     const [isLoading, setIsLoading] = useState(false);
//     const [sortOrder, setSortOrder] = useState('DESC');
    
//     // NUEVO: Estado para el filtro de "Solo con teléfono" (Default: true)
//     const [showOnlyWithPhone, setShowOnlyWithPhone] = useState(true);

//     // ----------------------------------------------------------------------------
//     // 2. LÓGICA DE CARGA DE DATOS
//     // ----------------------------------------------------------------------------

//     const loadInitialData = async () => {
//         setIsLoading(true);
//         try {
//             const response = await BLGetMessagesPage({ limit: PAGE_SIZE, cursor: null, sort: sortOrder });
//             if (!response.success) throw new Error(response.message);

//             setMessagesList(response.data.items || []);
//             setNextCursor(response.data.nextCursor);
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Listado actualizado.' });
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error al cargar mensajes.' });
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     const loadMoreMessages = async () => {
//         if (!nextCursor) {
//             showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay más mensajes antiguos por cargar.' });
//             return;
//         }
//         if (isLoading) return;

//         setIsLoading(true);
//         try {
//             const response = await BLGetMessagesPage({ limit: PAGE_SIZE, cursor: nextCursor, sort: sortOrder });
//             if (!response.success) throw new Error(response.message);

//             setMessagesList(prev => [...prev, ...response.data.items]);
//             setNextCursor(response.data.nextCursor);
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar historial.' });
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     useEffect(() => { loadInitialData(); }, [sortOrder]);

//     // ----------------------------------------------------------------------------
//     // 3. FUNCIONALIDADES ESPECÍFICAS
//     // ----------------------------------------------------------------------------

//     const copyPhoneNumberToClipboard = (phoneNumber) => {
//         if (!phoneNumber) return;
//         navigator.clipboard.writeText(phoneNumber)
//             .then(() => showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Teléfono copiado.' }))
//             .catch(() => showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al copiar.' }));
//     };

//     const downloadExcelReport = async () => {
//         if (isLoading) return;
//         setIsLoading(true);
//         showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte filtrado...' });

//         try {
//             // Traemos un lote grande para el reporte
//             const response = await BLGetMessagesPage({ limit: 5000, cursor: null, sort: 'DESC' });
            
//             if (!response.success || !response.data.items.length) {
//                 throw new Error("No hay datos para exportar.");
//             }

//             // AJUSTE: Filtrar antes de exportar si el switch está activo
//             let rawData = response.data.items;
            
//             if (showOnlyWithPhone) {
//                 // Filtramos solo los que tienen teléfono válido
//                 rawData = rawData.filter(item => item.phone && item.phone.trim() !== "");
//             }

//             if (rawData.length === 0) {
//                 throw new Error("No se encontraron mensajes con teléfono para exportar.");
//             }

//             // Mapeo para Excel
//             const dataToExport = rawData.map(item => ({
//                 ID: item.id,
//                 Fecha: formatFullDate(item.createdAtUtc || item.date),
//                 Mensaje: item.message,
//                 Remitente: item.sender || "Anónimo",
//                 Telefono: item.phone || "N/A"
//             }));

//             const worksheet = XLSX.utils.json_to_sheet(dataToExport);
//             const workbook = XLSX.utils.book_new();
//             XLSX.utils.book_append_sheet(workbook, worksheet, "Contactos"); // Nombre de hoja actualizado

//             const dateStr = new Date().toISOString().slice(0,10);
//             const fileName = showOnlyWithPhone ? `Reporte_Contactos_${dateStr}.xlsx` : `Reporte_Mensajes_${dateStr}.xlsx`;
            
//             XLSX.writeFile(workbook, fileName);

//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Reporte descargado (${dataToExport.length} registros).` });

//         } catch (error) {
//             console.error(error);
//             showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error al generar el Excel.' });
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     const handleReturn = () => { if (getOutOfTheDynamic) getOutOfTheDynamic(); };

//     // ----------------------------------------------------------------------------
//     // 4. PREPARACIÓN DE VISTA (FILTRO)
//     // ----------------------------------------------------------------------------
    
//     // Filtramos la lista visualmente según el estado del Switch
//     const displayedMessages = showOnlyWithPhone 
//         ? messagesList.filter(msg => msg.phone && msg.phone.trim() !== "")
//         : messagesList;

//     // ----------------------------------------------------------------------------
//     // 5. RENDERIZADO
//     // ----------------------------------------------------------------------------
//     return (
//         <SecondaryBase actionToExecute={handleReturn}>
            
//             {/* --- ENCABEZADO Y CONTROLES --- */}
//             <div className='d-flex flex-column px-4 mt-3'>
                
//                 <div className="d-flex justify-content-between align-items-center mb-3">
//                     <div>
//                         <p className='Admin__Messages__Text--Title'><i className='bi bi-table'></i> Tablero de Contactos</p>
//                         <p className='Admin__Messages__Text--Small'>
//                             Mostrando: {displayedMessages.length} {showOnlyWithPhone ? '(Solo con teléfono)' : '(Todos)'}
//                         </p>
//                     </div>
                    
//                     <button type="button" className='Btn__Adsl--RedOutlineTransparent' onClick={downloadExcelReport} disabled={isLoading}>
//                         <i className='bi bi-file-earmark-excel-fill text-success'></i> 
//                         {showOnlyWithPhone ? (
//                             displayedMessages.length === 1 ? (
//                                 ' Descargar 1 contacto'
//                             ) : (
//                                 ` Descargar ${displayedMessages.length} contactos`
//                             )
//                         ) : (
//                             ' Descargar todo'
//                         )}
//                     </button>
//                 </div>

//                 {/* Barra de Herramientas: Filtros y Orden */}
//                 <div className='d-flex justify-content-between align-items-center border-bottom pb-3 border-secondary'>
                    
//                     {/* Switch de Filtro */}
//                     <div className="form-check form-switch">
//                         <input 
//                             className="form-check-input" 
//                             type="checkbox" 
//                             role="switch" 
//                             id="flexSwitchCheckChecked" 
//                             checked={showOnlyWithPhone}
//                             onChange={(e) => setShowOnlyWithPhone(e.target.checked)}
//                         />
//                         <label className="form-check-label small" htmlFor="flexSwitchCheckChecked">
//                             Mostrar solo mensajes con teléfono
//                         </label>
//                     </div>

//                     <div className='d-flex align-items-center'>
//                         <span className="me-2 small">Ordenar:</span>
//                         <div className="btn-group btn-group-sm">
//                             <button type="button" className={`btn ${sortOrder === 'ASC' ? 'btn-danger' : 'btn-outline-secondary'}`} onClick={() => setSortOrder('ASC')}>Antiguos</button>
//                             <button type="button" className={`btn ${sortOrder === 'DESC' ? 'btn-danger' : 'btn-outline-secondary'}`} onClick={() => setSortOrder('DESC')}>Nuevos</button>
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* --- LISTADO DE TARJETAS --- */}
//             <section className='Admin__Messages__Library Admin__Messages__SpaceUp'>
//                 {displayedMessages.length > 0 ? (
//                     displayedMessages.map((msg) => (
//                         <div key={msg.id} className="Admin__Messages__Library__Message" style={{ height: 'auto', minHeight: '250px' }}>
                            
//                             <div className="d-flex flex-column justify-content-between h-100">
                                
//                                 <div className="text-center border-bottom border-secondary pb-2 mb-2">
//                                     <p className='Admin__Messages__Text--Small fw-bold mb-0'>#{msg.id}</p>
//                                     <p className='small text-muted mb-0' style={{ fontSize: '0.7rem' }}>
//                                         {formatFullDate(msg.date || msg.createdAtUtc)}
//                                     </p>
//                                 </div>

//                                 <div className="text-center flex-grow-1 d-flex flex-column justify-content-center">
//                                     <p className="fw-bold fs-5 mb-2 fst-italic">"{msg.message}"</p>
//                                     <p className="text-info small mb-0">- {msg.sender || "Anónimo"}</p>
//                                 </div>

//                                 <div className="mt-3 pt-2 border-top border-secondary text-center">
//                                     {msg.phone ? (
//                                         <div className="d-flex justify-content-center align-items-center gap-2">
//                                             <span className="badge bg-dark text-white border border-secondary fw-normal p-2">
//                                                 <i className="bi bi-whatsapp text-success me-1"></i> {msg.phone}
//                                             </span>
//                                             <button 
//                                                 type="button" 
//                                                 className="btn btn-sm btn-outline-light" 
//                                                 onClick={() => copyPhoneNumberToClipboard(msg.phone)}
//                                                 title="Copiar teléfono"
//                                             >
//                                                 <i className="bi bi-clipboard"></i>
//                                             </button>
//                                         </div>
//                                     ) : (
//                                         <span className="text-muted small fst-italic opacity-50">Sin teléfono</span>
//                                     )}
//                                 </div>

//                             </div>
//                         </div>
//                     ))
//                 ) : (
//                     <div className="w-100 text-center py-5">
//                         <p className="text-muted fs-4">
//                             {showOnlyWithPhone 
//                                 ? "No hay mensajes con teléfono en esta página." 
//                                 : "No hay mensajes cargados."}
//                         </p>
                        
//                         {/* Mensaje de ayuda si el filtro está vacío */}
//                         {showOnlyWithPhone && (
//                             <p className="text-muted small">Intenta cargar más historial o desactiva el filtro.</p>
//                         )}

//                         <button type="button" className="Btn__Adsl--RedOutlineTransparent mt-3" onClick={() => loadMoreMessages()} disabled={isLoading}>
//                             Intentar cargar más del historial
//                         </button>
//                     </div>
//                 )}
//             </section>

//             {/* --- BOTÓN CARGAR MÁS --- */}
//             {nextCursor && (
//                 <div className="d-flex justify-content-center my-5">
//                     <button type="button" className="Btn__Adsl--RedOutlineTransparent px-5" onClick={loadMoreMessages} disabled={isLoading}>
//                         {isLoading ? (
//                             <> <span className="spinner-border spinner-border-sm me-2"></span> Cargando...</>
//                         ) : (
//                             <> <i className="bi bi-arrow-clockwise"></i> Cargar más mensajes</>
//                         )}
//                     </button>
//                 </div>
//             )}

//         </SecondaryBase>
//     );
// };

// export { MessagesBoard };


















// // ==============================================================================
// // CONFIGURACIÓN Y UTILIDADES
// // ==============================================================================

// const PAGE_SIZE = 30; // Cantidad de mensajes por carga

// // Función para formatear la fecha visualmente
// const formatFullDate = (dateString) => {
//     if (!dateString) return "Fecha no disponible";
//     const date = new Date(dateString);
//     return date.toLocaleString('es-MX', {
//         day: '2-digit', month: '2-digit', year: 'numeric',
//         hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
//     }) + " hrs";
// };

// /**
//  * COMPONENTE: MessagesBoard
//  * Descripción: Tablero de lectura para consultar el historial de mensajes,
//  * copiar teléfonos y descargar reportes en Excel.
//  */
// const MessagesBoard = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage();

//     // ----------------------------------------------------------------------------
//     // 1. ESTADOS
//     // ----------------------------------------------------------------------------
//     const [messagesList, setMessagesList] = useState([]);
//     const [nextCursor, setNextCursor] = useState(null);
//     const [isLoading, setIsLoading] = useState(false);
//     const [sortOrder, setSortOrder] = useState('DESC'); // 'DESC' = Más nuevos primero

//     // ----------------------------------------------------------------------------
//     // 2. LÓGICA DE CARGA DE DATOS
//     // ----------------------------------------------------------------------------

//     /**
//      * Carga la primera página de mensajes según el orden seleccionado.
//      */
//     const loadInitialData = async () => {
//         setIsLoading(true);
//         try {
//             const response = await BLGetMessagesPage({ limit: PAGE_SIZE, cursor: null, sort: sortOrder });
//             if (!response.success) throw new Error(response.message);

//             setMessagesList(response.data.items || []);
//             setNextCursor(response.data.nextCursor);
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Listado actualizado correctamente.' });
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error al cargar mensajes.' });
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     /**
//      * Carga el siguiente bloque de mensajes (Paginación).
//      */
//     const loadMoreMessages = async () => {
//         if (!nextCursor) {
//             showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay más mensajes antiguos por cargar.' });
//             return;
//         }
//         if (isLoading) return;

//         setIsLoading(true);
//         try {
//             const response = await BLGetMessagesPage({ limit: PAGE_SIZE, cursor: nextCursor, sort: sortOrder });
//             if (!response.success) throw new Error(response.message);

//             setMessagesList(prev => [...prev, ...response.data.items]);
//             setNextCursor(response.data.nextCursor);
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar historial.' });
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     // Recargar datos cuando cambia el ordenamiento
//     useEffect(() => {
//         loadInitialData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [sortOrder]);

//     // ----------------------------------------------------------------------------
//     // 3. FUNCIONALIDADES ESPECÍFICAS (Excel y Portapapeles)
//     // ----------------------------------------------------------------------------

//     /**
//      * Copia el número de teléfono al portapapeles del sistema.
//      * @param {string} phoneNumber - El número a copiar.
//      */
//     const copyPhoneNumberToClipboard = (phoneNumber) => {
//         if (!phoneNumber) return;
//         navigator.clipboard.writeText(phoneNumber)
//             .then(() => showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Teléfono copiado al portapapeles.' }))
//             .catch(() => showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudo copiar el teléfono.' }));
//     };

//     /**
//      * Descarga TODOS los mensajes disponibles en un archivo Excel (.xlsx).
//      * Nota: Hace una petición especial para traer un lote grande (ej. 1000) o todos.
//      */
//     const downloadExcelReport = async () => {
//         if (isLoading) return;
//         setIsLoading(true);
//         showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte, por favor espera...' });

//         try {
//             // Petición para traer una gran cantidad de datos para el reporte (limit: 5000 por ejemplo)
//             // Si tienes un endpoint específico de "GetAll", úsalo aquí. Si no, usamos el de paginación con límite alto.
//             const response = await BLGetMessagesPage({ limit: 5000, cursor: null, sort: 'DESC' });
            
//             if (!response.success || !response.data.items.length) {
//                 throw new Error("No hay datos para exportar.");
//             }

//             const dataToExport = response.data.items.map(item => ({
//                 ID: item.id,
//                 Fecha: formatFullDate(item.createdAtUtc || item.date),
//                 Mensaje: item.message,
//                 Remitente: item.sender || "Anónimo",
//                 Telefono: item.phone || "N/A"
//             }));

//             // Crear libro de trabajo y hoja
//             const worksheet = XLSX.utils.json_to_sheet(dataToExport);
//             const workbook = XLSX.utils.book_new();
//             XLSX.utils.book_append_sheet(workbook, worksheet, "Mensajes");

//             // Generar nombre de archivo con fecha
//             const dateStr = new Date().toISOString().slice(0,10);
//             XLSX.writeFile(workbook, `Reporte_Mensajes_${dateStr}.xlsx`);

//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte descargado correctamente.' });

//         } catch (error) {
//             console.error(error);
//             showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al generar el Excel.' });
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     /**
//      * Regresa al dashboard principal.
//      */
//     const handleReturn = () => {
//         if (getOutOfTheDynamic) getOutOfTheDynamic();
//     };

//     // ----------------------------------------------------------------------------
//     // 4. RENDERIZADO
//     // ----------------------------------------------------------------------------
//     return (
//         <SecondaryBase actionToExecute={handleReturn}>
            
//             {/* --- ENCABEZADO Y CONTROLES --- */}
//             <div className='d-flex flex-column px-4 mt-3'>
                
//                 {/* Título y Totales */}
//                 <div className="d-flex justify-content-between align-items-center mb-3">
//                     <div>
//                         <p className='Admin__Messages__Text--Title'><i className='bi bi-table'></i> Tablero de Mensajes</p>
//                         <p className='Admin__Messages__Text--Small'>Total en vista: {messagesList.length}</p>
//                     </div>
                    
//                     {/* Botón Excel */}
//                     <button type="button" className='Btn__Adsl--RedOutlineTransparent' onClick={downloadExcelReport} disabled={isLoading}>
//                         <i className='bi bi-file-earmark-excel-fill text-success'></i> Descargar Excel Completo
//                     </button>
//                 </div>

//                 {/* Filtros de Ordenamiento */}
//                 <div className='d-flex justify-content-end align-items-center border-bottom pb-3 border-secondary'>
//                     <span className="me-2 text-white small">Ordenar por fecha:</span>
//                     <div className="btn-group btn-group-sm">
//                         <button type="button" className={`btn ${sortOrder === 'ASC' ? 'btn-danger' : 'btn-outline-secondary text-white'}`} onClick={() => setSortOrder('ASC')}>Antiguos primero</button>
//                         <button type="button" className={`btn ${sortOrder === 'DESC' ? 'btn-danger' : 'btn-outline-secondary text-white'}`} onClick={() => setSortOrder('DESC')}>Nuevos primero</button>
//                     </div>
//                 </div>
//             </div>

//             {/* --- LISTADO DE TARJETAS --- */}
//             <section className='Admin__Messages__Library Admin__Messages__SpaceUp'>
//                 {messagesList.length > 0 ? (
//                     messagesList.map((msg) => (
//                         <div key={msg.id} className="Admin__Messages__Library__Message" style={{ height: 'auto', minHeight: '250px' }}>
                            
//                             {/* Contenido de la Tarjeta */}
//                             <div className="d-flex flex-column justify-content-between h-100">
                                
//                                 {/* Encabezado Tarjeta: ID y Fecha */}
//                                 <div className="text-center border-bottom border-secondary pb-2 mb-2">
//                                     <p className='Admin__Messages__Text--Small fw-bold mb-0'>#{msg.id}</p>
//                                     <p className='small text-muted mb-0' style={{ fontSize: '0.7rem' }}>
//                                         {formatFullDate(msg.date || msg.createdAtUtc)}
//                                     </p>
//                                 </div>

//                                 {/* Cuerpo: Mensaje y Autor */}
//                                 <div className="text-center flex-grow-1 d-flex flex-column justify-content-center">
//                                     <p className="fw-bold fs-5 mb-2 fst-italic">"{msg.message}"</p>
//                                     <p className="text-info small mb-0">- {msg.sender || "Anónimo"}</p>
//                                 </div>

//                                 {/* Pie: Teléfono y Copiar */}
//                                 <div className="mt-3 pt-2 border-top border-secondary text-center">
//                                     {msg.phone ? (
//                                         <div className="d-flex justify-content-center align-items-center gap-2">
//                                             <span className="badge bg-dark text-white border border-secondary fw-normal p-2">
//                                                 <i className="bi bi-whatsapp text-success me-1"></i> {msg.phone}
//                                             </span>
//                                             <button 
//                                                 type="button" 
//                                                 className="btn btn-sm btn-outline-light" 
//                                                 onClick={() => copyPhoneNumberToClipboard(msg.phone)}
//                                                 title="Copiar teléfono"
//                                             >
//                                                 <i className="bi bi-clipboard"></i>
//                                             </button>
//                                         </div>
//                                     ) : (
//                                         <span className="text-muted small fst-italic">Sin teléfono</span>
//                                     )}
//                                 </div>

//                             </div>
//                         </div>
//                     ))
//                 ) : (
//                     <div className="w-100 text-center py-5">
//                         <p className="text-muted fs-4">No hay mensajes cargados.</p>
//                         <button type="button" className="Btn__Adsl--RedOutlineTransparent" onClick={loadInitialData}>Recargar listado</button>
//                     </div>
//                 )}
//             </section>

//             {/* --- BOTÓN CARGAR MÁS --- */}
//             {nextCursor && (
//                 <div className="d-flex justify-content-center my-5">
//                     <button type="button" className="Btn__Adsl--RedOutlineTransparent px-5" onClick={loadMoreMessages} disabled={isLoading}>
//                         {isLoading ? (
//                             <> <span className="spinner-border spinner-border-sm me-2"></span> Cargando... </>
//                         ) : (
//                             <> <i className="bi bi-plus-circle-dotted"></i> Cargar más mensajes </>
//                         )}
//                     </button>
//                 </div>
//             )}

//         </SecondaryBase>
//     );
// };

// export { MessagesBoard };